<template>
  <div>
    <Layout ref="layoutRef" :selectedOption="1" />
    <h4>Conversor de archivos de carga (→ CSV)</h4>
    <hr />

    <ul class="nav nav-tabs mb-3">
      <li class="nav-item" v-for="(m, clave) in modos" :key="clave">
        <button
          type="button"
          class="nav-link"
          :class="{ active: modo === clave }"
          :disabled="cargando"
          @click="cambiarModo(clave)"
        >
          {{ m.etiqueta }}
        </button>
      </li>
    </ul>

    <div class="card shadow-sm">
      <div class="card-body">
        <!-- Descripción de cada opción -->
        <p class="text-muted mb-3" v-if="modo === 'xlsx_completo'">
          Cargue uno o varios archivos <code>.xlsx</code> completos ({{ columnasEsperadas.length }} columnas).
          Se genera un <code>.csv</code> por archivo {{ textoCabecera }}.
        </p>
        <p class="text-muted mb-3" v-else-if="modo === 'xlsx_incompleto'">
          Cargue uno o varios archivos <code>.xlsx</code> incompletos (con columnas vacías). Se genera un
          <code>.csv</code> por archivo reemplazando la primera fila (cabecera) por los encabezados estándar,
          colocando las fechas del período y completando los campos que vengan vacíos.
        </p>
        <p class="text-muted mb-3" v-else-if="modo === 'csv_completo'">
          Cargue uno o varios archivos <code>.csv</code> completos ({{ columnasEsperadas.length }} columnas).
          Se genera un <code>.csv</code> por archivo {{ textoCabecera }}. El resto del archivo no se
          modifica (mismo separador y codificación), salvo las correcciones automáticas indicadas abajo.
        </p>
        <p class="text-muted mb-3" v-else>
          Cargue uno o varios archivos <code>.csv</code> incompletos (con columnas vacías). Se reemplaza la
          primera línea (cabecera) por los encabezados estándar, se colocan las fechas del período y se
          completan los campos que vengan vacíos. El resultado se guarda en UTF-8.
        </p>

        <p class="small text-muted mb-3">
          Correcciones automáticas: los diagnósticos (<code>noDiagPri</code>, <code>noDiagSec</code>) que no
          sean un código CIE-10 (<code>F41</code>, <code>F412</code>, <code>F41.2</code>) se reemplazan por
          <code>-</code>; un <code>tipo</code> que no sea un único carácter (vacío o de más de uno) se reemplaza por <code>S</code>;
          y una cédula (<code>noPaciente</code>) de 9 dígitos se completa con un <code>0</code> a la izquierda.
        </p>

        <!-- Cabecera del archivo (opciones "completo") -->
        <div v-if="!esIncompleto" class="mb-3">
          <span class="fw-bold me-3">El archivo</span>
          <div class="form-check form-check-inline">
            <input
              id="conCabecera"
              class="form-check-input"
              type="radio"
              name="cabecera"
              :value="true"
              v-model="conCabecera"
              :disabled="cargando"
            />
            <label class="form-check-label" for="conCabecera">Con cabecera</label>
          </div>
          <div class="form-check form-check-inline">
            <input
              id="sinCabecera"
              class="form-check-input"
              type="radio"
              name="cabecera"
              :value="false"
              v-model="conCabecera"
              :disabled="cargando"
            />
            <label class="form-check-label" for="sinCabecera">Sin cabecera</label>
          </div>
        </div>

        <!-- Período y valores por defecto (opciones "incompleto") -->
        <div v-if="esIncompleto">
          <div class="row mb-3 align-items-center">
            <div class="col-auto">
              <label class="form-label fw-bold mb-0" for="periodo">Año / mes de las atenciones</label>
            </div>
            <div class="col-auto">
              <input
                id="periodo"
                type="month"
                class="form-control"
                v-model="periodo"
                :disabled="cargando"
                required
              />
            </div>
            <div class="col-auto small text-muted" v-if="fechasPeriodo">
              fIngreso: {{ fechasPeriodo.ingreso }} · fSalida: {{ fechasPeriodo.salida }} ·
              fAplicacion: {{ fechasPeriodo.aplicacion }}
            </div>
          </div>

          <details class="mb-3 small">
            <summary class="text-muted">Valores con los que se completan los campos vacíos</summary>
            <div class="d-flex flex-wrap gap-1 mt-2">
              <span v-for="v in valoresCentro" :key="v.campo" class="badge bg-light text-dark border">
                {{ v.campo }} = {{ v.valor }}
              </span>
            </div>
          </details>
        </div>

        <!-- Zona de carga -->
        <div
          class="border border-2 rounded p-4 text-center mb-3"
          :class="arrastrando ? 'border-primary bg-light' : 'border-secondary-subtle'"
          style="cursor:pointer; border-style:dashed !important"
          @dragover.prevent="arrastrando = true"
          @dragleave.prevent="arrastrando = false"
          @drop.prevent="onDrop"
          @click="$refs.inputArchivo.click()"
        >
          <input
            ref="inputArchivo"
            type="file"
            class="d-none"
            multiple
            :accept="acceptModo"
            @change="onSeleccionar"
          />
          <span class="material-icons" style="font-size:40px">upload_file</span>
          <div>
            <strong>Arrastre los archivos aquí</strong>
            <div class="text-muted small">o haga clic para seleccionarlos ({{ extensionModo }}, puede elegir varios)</div>
          </div>
        </div>

        <!-- Cola de archivos -->
        <div class="table-responsive mb-3" v-if="archivos.length">
          <table class="table table-sm table-hover align-middle mb-0">
            <thead>
              <tr>
                <th>#</th>
                <th>Archivo</th>
                <th>Tamaño</th>
                <th>Estado</th>
                <th class="text-end">Filas procesadas</th>
                <th class="text-end">Acciones</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(a, i) in archivos" :key="a.id">
                <td>{{ i + 1 }}</td>
                <td class="text-nowrap">{{ a.file.name }}</td>
                <td class="text-nowrap">{{ tamano(a.file.size) }}</td>
                <td>
                  <span class="badge" :class="badgeEstado(a.estado)">{{ textoEstado(a.estado) }}</span>
                  <span v-if="a.estado === 'procesando'" class="spinner-border spinner-border-sm ms-1"></span>
                  <span
                    v-if="a.estado === 'ok' && a.correcciones && a.correcciones.total"
                    class="badge bg-info text-dark ms-1"
                    :title="detalleCorrecciones(a.correcciones)"
                  >
                    {{ a.correcciones.total }} {{ a.correcciones.total === 1 ? "valor corregido" : "valores corregidos" }}
                  </span>
                  <span
                    v-if="a.estado === 'ok' && a.intercambios && a.intercambios.length"
                    class="badge bg-warning text-dark ms-1"
                    :title="textoIntercambios(a)"
                  >
                    Columnas intercambiadas
                  </span>
                  <template v-if="a.estado === 'ok' && a.validacion">
                    <span v-if="a.validacion.total" class="badge bg-warning text-dark ms-1">
                      {{ a.validacion.total }} {{ a.validacion.total === 1 ? "error" : "errores" }} de validación
                    </span>
                    <span v-else class="badge bg-light text-success border ms-1">Validado</span>
                  </template>
                  <div v-if="a.mensaje" class="small text-danger">{{ a.mensaje }}</div>
                </td>
                <td class="text-end">{{ a.estado === 'ok' ? a.filas : '' }}</td>
                <td class="text-end text-nowrap">
                  <button
                    v-if="a.estado === 'ok'"
                    type="button"
                    class="btn btn-outline-primary btn-sm me-1"
                    title="Ver vista previa"
                    @click="verVista(a)"
                  >
                    <span class="material-icons" style="font-size:16px;vertical-align:middle">visibility</span>
                  </button>
                  <a
                    v-if="a.estado === 'ok'"
                    class="btn btn-outline-success btn-sm me-1"
                    :href="a.url"
                    :download="a.nombre"
                    :title="'Descargar ' + a.nombre"
                  >
                    <span class="material-icons" style="font-size:16px;vertical-align:middle">download</span>
                  </a>
                  <button
                    type="button"
                    class="btn btn-outline-danger btn-sm"
                    title="Quitar de la lista"
                    :disabled="cargando"
                    @click="quitarArchivo(a)"
                  >
                    <span class="material-icons" style="font-size:16px;vertical-align:middle">delete</span>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div
          v-if="archivosOk.length && !cargando"
          class="alert d-flex align-items-center justify-content-between"
          :class="totalErrores ? 'alert-warning' : 'alert-success'"
        >
          <span>
            <strong>{{ archivosOk.length }}</strong> de {{ archivos.length }}
            {{ archivos.length === 1 ? "archivo convertido" : "archivos convertidos" }}
            ({{ totalFilas }} filas de datos en total).
            <template v-if="totalErrores">
              <strong>{{ totalErrores }}</strong> errores de validación: use el botón
              <span class="material-icons" style="font-size:15px;vertical-align:middle">visibility</span>
              para ver el detalle.
            </template>
            <template v-else>Sin errores de validación.</template>
          </span>
          <button
            v-if="archivosOk.length > 1"
            type="button"
            class="btn btn-sm btn-success"
            @click="descargarTodos"
          >
            <span class="material-icons me-1" style="font-size:16px;vertical-align:middle">download</span>
            Descargar todos
          </button>
        </div>

        <div class="d-flex gap-2">
          <button
            type="button"
            class="btn btn-primary"
            :disabled="!puedeConvertir"
            @click="convertir"
          >
            <span v-if="cargando" class="spinner-border spinner-border-sm me-1"></span>
            <span v-else class="material-icons me-1" style="font-size:18px;vertical-align:middle">sync_alt</span>
            {{ textoBoton }}
          </button>
          <button
            type="button"
            class="btn btn-secondary"
            :disabled="cargando || !archivos.length"
            @click="limpiar"
          >
            Limpiar
          </button>
        </div>

        <!-- Encabezados que tendrá el CSV -->
        <hr />
        <h6 class="fw-bold">Encabezados del CSV</h6>
        <div class="d-flex flex-wrap gap-1">
          <span
            v-for="(col, i) in columnasEsperadas"
            :key="col"
            class="badge bg-secondary"
          >
            {{ i + 1 }}. {{ col }}
          </span>
        </div>
      </div>
    </div>

    <!-- Vista previa del CSV generado -->
    <div class="card shadow-sm mt-3" v-if="vista">
      <div class="card-body">
        <h5 class="mb-1">
          Vista previa del CSV generado
          <span v-if="vistaNombre" class="text-muted fs-6">— {{ vistaNombre }}</span>
        </h5>
        <p class="text-muted small mb-3">
          {{ vista.total }} filas de datos.
          <template v-if="vista.validacion && vista.validacion.total">
            Use la lista de la columna <strong>Validación</strong> para ver solo las filas con un valor concreto (OK o el error).
          </template>
        </p>

        <!-- Intercambiar dos columnas del CSV generado y exportarlo -->
        <div class="border rounded p-2 mb-3 bg-light" v-if="vistaItem">
          <div class="d-flex flex-wrap align-items-center gap-2">
            <strong class="small">Intercambiar columnas</strong>
            <select v-model="swap.a" class="form-select form-select-sm w-auto" :disabled="intercambiando" aria-label="Primera columna">
              <option value="">Columna 1...</option>
              <option v-for="c in columnasEsperadas" :key="c" :value="c" :disabled="c === swap.b">{{ c }}</option>
            </select>
            <span class="material-icons" style="font-size:18px">swap_horiz</span>
            <select v-model="swap.b" class="form-select form-select-sm w-auto" :disabled="intercambiando" aria-label="Segunda columna">
              <option value="">Columna 2...</option>
              <option v-for="c in columnasEsperadas" :key="c" :value="c" :disabled="c === swap.a">{{ c }}</option>
            </select>
            <button type="button" class="btn btn-warning btn-sm" :disabled="!puedeIntercambiar" @click="intercambiarColumnas">
              Intercambiar
            </button>
            <button type="button" class="btn btn-success btn-sm d-flex align-items-center gap-1" @click="exportarVista">
              <span class="material-icons" style="font-size:16px">download</span> Exportar CSV
            </button>
            <button
              v-if="vistaItem.original"
              type="button"
              class="btn btn-outline-secondary btn-sm"
              :disabled="intercambiando"
              @click="restaurarOriginal"
            >
              Restaurar original
            </button>
            <button v-if="swap.a || swap.b" type="button" class="btn btn-link btn-sm" @click="limpiarSwap">
              Limpiar selección
            </button>
          </div>
          <div class="small text-muted mt-1">
            Pulse el icono
            <span class="material-icons" style="font-size:14px;vertical-align:middle">swap_horiz</span>
            en el encabezado de dos columnas (o elíjalas en las listas) y luego <strong>Intercambiar</strong>.
            Se intercambian los <strong>valores</strong> de las dos columnas; los encabezados estándar no cambian.
          </div>
          <div class="small mt-1" v-if="vistaItem.intercambios.length">
            <span class="badge bg-warning text-dark">Intercambios aplicados</span>
            {{ textoIntercambios(vistaItem) }} — el CSV de este archivo ya incluye el cambio ({{ vistaItem.nombre }}).
          </div>
        </div>

        <!-- Tabla propia (mismo esquema que Trámites): ordenar, filtrar por columna y paginar -->
        <div class="d-flex justify-content-end align-items-center gap-2 mb-2">
          <label class="form-label mb-0 small fw-bold">Mostrar</label>
          <select v-model.number="tabla.porPagina" class="form-select form-select-sm" style="width:75px" @change="tabla.pagina = 1">
            <option :value="10">10</option>
            <option :value="25">25</option>
            <option :value="50">50</option>
            <option :value="100">100</option>
          </select>
          <span class="small text-muted">registros</span>
        </div>

        <div class="table-responsive tabla-sin-saltos" v-if="vista.filas.length > 0">
          <table class="table table-hover table-bordered table-sm">
            <thead class="table-dark">
              <!-- Fila de encabezados -->
              <tr>
                <th
                  v-for="col in vista.columnas"
                  :key="col.field"
                  class="th-sort"
                  :class="{ 'th-swap': esColumnaSwap(col.field) }"
                  @click="ordenarVistaPor(col.field)"
                >
                  {{ col.label }} <span>{{ iconOrden(col.field) }}</span>
                  <button
                    v-if="esColumnaDato(col.field)"
                    type="button"
                    class="btn btn-sm p-0 ms-1 swap-btn"
                    :class="{ activo: esColumnaSwap(col.field) }"
                    :title="esColumnaSwap(col.field) ? 'Quitar de la selección' : 'Señalar para intercambiar'"
                    @click.stop="alternarColumnaSwap(col.field)"
                  >
                    <span class="material-icons" style="font-size:16px;vertical-align:middle">swap_horiz</span>
                  </button>
                </th>
              </tr>
              <!-- Fila de filtros -->
              <tr>
                <th v-for="col in vista.columnas" :key="col.field">
                  <!-- Validación: lista con los valores distintos de la columna -->
                  <select
                    v-if="col.field === '_validacion'"
                    id="filtroValidacion"
                    v-model="tabla.validacion"
                    @change="tabla.pagina = 1"
                    class="form-select form-select-sm"
                  >
                    <option value="">Todos</option>
                    <option v-for="o in opcionesValidacion" :key="o.valor" :value="o.valor">{{ o.texto }}</option>
                  </select>
                  <input
                    v-else
                    v-model="tabla.filtros[col.field]"
                    @input="tabla.pagina = 1"
                    class="form-control form-control-sm"
                    placeholder="Filtrar..."
                  />
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="fila in filasPagina" :key="fila._id">
                <td
                  v-for="col in vista.columnas"
                  :key="col.field"
                  :class="{
                    'td-swap': esColumnaSwap(col.field),
                    'table-danger': fila._errores && fila._errores.includes(col.field),
                    'text-danger fw-bold': col.field === '_validacion' && fila._errores && fila._errores.length,
                  }"
                >{{ fila[col.field] }}</td>
              </tr>
              <tr v-if="!filasPagina.length">
                <td :colspan="vista.columnas.length" class="text-center text-muted">Sin resultados.</td>
              </tr>
            </tbody>
            <tfoot>
              <tr class="table-secondary">
                <td :colspan="vista.columnas.length" class="text-end fw-bold small">
                  Mostrando {{ filasPagina.length }} de {{ filasOrdenadas.length }} registros
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
        <div v-else class="alert alert-info text-center">El archivo generado no tiene filas de datos.</div>

        <!-- Paginación -->
        <div class="d-flex justify-content-between align-items-center mt-3" v-if="vista.filas.length > 0">
          <span class="small text-muted">
            Página {{ paginaActual }} de {{ totalPaginas }} — {{ filasOrdenadas.length }} registros
          </span>
          <nav aria-label="Paginación">
            <ul class="pagination pagination-sm mb-0">
              <li class="page-item" :class="{ disabled: paginaActual === 1 }">
                <a class="page-link" href="#" @click.prevent="irAPagina(1)">«</a>
              </li>
              <li class="page-item" :class="{ disabled: paginaActual === 1 }">
                <a class="page-link" href="#" @click.prevent="irAPagina(paginaActual - 1)">‹</a>
              </li>
              <li
                v-for="p in paginasVisibles"
                :key="p"
                class="page-item"
                :class="{ active: p === paginaActual, disabled: p === '...' }"
              >
                <a class="page-link" href="#" @click.prevent="p !== '...' && irAPagina(p)">{{ p }}</a>
              </li>
              <li class="page-item" :class="{ disabled: paginaActual === totalPaginas }">
                <a class="page-link" href="#" @click.prevent="irAPagina(paginaActual + 1)">›</a>
              </li>
              <li class="page-item" :class="{ disabled: paginaActual === totalPaginas }">
                <a class="page-link" href="#" @click.prevent="irAPagina(totalPaginas)">»</a>
              </li>
            </ul>
          </nav>
        </div>
      </div>
    </div>

    <!-- Errores de validación del CSV mostrado -->
    <div class="card shadow-sm mt-3 border-warning" v-if="validacionVista">
      <div class="card-body">
        <h5 class="mb-1">
          Errores de validación
          <span v-if="vistaNombre" class="text-muted fs-6">— {{ vistaNombre }}</span>
        </h5>
        <p class="text-muted small mb-2">
          {{ validacionVista.total }} {{ validacionVista.total === 1 ? "error" : "errores" }} en
          {{ validacionVista.filasConError }} de {{ validacionVista.registros }} filas.
          <template v-if="validacionVista.total > validacionVista.errores.length">
            Se listan los primeros {{ validacionVista.errores.length }}.
          </template>
        </p>
        <div class="d-flex flex-wrap gap-1 mb-3">
          <span v-for="r in validacionVista.resumen" :key="r.campo" class="badge bg-warning text-dark">
            {{ r.campo }}: {{ r.cantidad }}
          </span>
        </div>

        <div class="tabla-sin-saltos">
        <DataTable
          :key="vistaNombre"
          :data="erroresFilas"
          :columns="columnasErrores"
          idField="_id"
          mensajeVacio="Sin errores."
          :acciones="false"
          :mostrarBotonTodos="false"
          :mostrarBotonNuevo="false"
          :mostrarEditar="false"
          :mostrarVer="false"
          :mostrarEliminar="false"
        />
        </div>
      </div>
    </div>

    <Footer />
  </div>
</template>

<script>
import Layout from "@/components/Layout.vue";
import Footer from "@/components/Footer.vue";
import DataTable from "@/components/DataTable.vue";
import api    from "@/services/api";
import Swal   from "sweetalert2";

const MAX_MB = 20;

const RUTA_COMPLETO   = "/tasks/xlsx-csv/convertir";
const RUTA_INCOMPLETO = "/tasks/xlsx-csv/convertir-incompleto";

// Opciones: formato del archivo de entrada × tipo (completo / incompleto)
const MODOS = {
  xlsx_completo:   { etiqueta: "XLSX Completo",   formato: "xlsx", incompleto: false },
  xlsx_incompleto: { etiqueta: "XLSX Incompleto", formato: "xlsx", incompleto: true  },
  csv_completo:    { etiqueta: "CSV Completo",    formato: "csv",  incompleto: false },
  csv_incompleto:  { etiqueta: "CSV Incompleto",  formato: "csv",  incompleto: true  },
};

const SEPARADORES = [";", ",", "\t"];
const LARGO_CEDULA = 10;

const MAX_ERRORES_LISTA = 1000; // errores que se listan en detalle (el conteo es siempre total)

// ── Reglas de validación de cada campo del CSV generado ──
// CIE-10: letra + 2 dígitos, y opcionalmente 1-2 caracteres con o sin punto.
// Válidos: F41, F412, F41.2, G56.0, Z00.6
const CIE10 = /^[A-Z]\d{2}(\.?[A-Z0-9]{1,2})?$/;
const NUMERO = /^\d+(\.\d+)?$/;

// Fecha real en formato YYYYMMDD
function fechaValida(v) {
  if (!/^\d{8}$/.test(v)) return false;
  const a = Number(v.slice(0, 4));
  const m = Number(v.slice(4, 6));
  const d = Number(v.slice(6, 8));
  if (a < 1900 || a > 2100 || m < 1 || m > 12 || d < 1) return false;
  return d <= new Date(a, m, 0).getDate();
}

// obligatorio: no puede venir vacío · valida: función sobre el valor (sin espacios) · detalle: mensaje
const REGLAS = {
  noPlanilla:    { obligatorio: true,  valida: v => /^\d+$/.test(v),        detalle: "debe ser numérico (entero)" },
  codVal:        { obligatorio: false },
  noPaciente:    { obligatorio: true,  valida: v => /^\d{10}$/.test(v),     detalle: "la cédula debe tener 10 dígitos" },
  nombre:        { obligatorio: true },
  fIngreso:      { obligatorio: true,  valida: fechaValida,                  detalle: "fecha inválida (formato YYYYMMDD)" },
  fSalida:       { obligatorio: true,  valida: fechaValida,                  detalle: "fecha inválida (formato YYYYMMDD)" },
  noDiagPri:     { obligatorio: true,  valida: v => v === "-" || CIE10.test(v), detalle: 'código CIE-10 inválido (o "-" si no hay)' },
  noDiagSec:     { obligatorio: false, valida: v => v === "-" || CIE10.test(v), detalle: 'código CIE-10 inválido (o "-" si no hay)' },
  lMuerto:       { obligatorio: true,  valida: v => v === "0" || v === "1",  detalle: "debe ser 0 o 1" },
  fTarifario:    { obligatorio: true,  valida: fechaValida,                  detalle: "fecha inválida (formato YYYYMMDD)" },
  noServicio:    { obligatorio: true },
  cantidad:      { obligatorio: true,  valida: v => NUMERO.test(v),          detalle: "debe ser numérico" },
  vSem:          { obligatorio: true,  valida: v => NUMERO.test(v),          detalle: "debe ser numérico" },
  vPaciente:     { obligatorio: true,  valida: v => NUMERO.test(v),          detalle: "debe ser numérico" },
  noModificador: { obligatorio: true,  valida: v => v.length <= 5,           detalle: "debe tener hasta 5 caracteres" },
  nombreMedIns:  { obligatorio: true },
  tipo:          { obligatorio: true,  valida: v => Array.from(v).length === 1, detalle: "debe ser un solo carácter" },
  fAplicacion:   { obligatorio: true,  valida: fechaValida,                  detalle: "fecha inválida (formato YYYYMMDD)" },
};

// ── Correcciones automáticas del CSV generado ──
// Diagnóstico que no es un código CIE-10 → "-" (también se pasa a mayúsculas: f41.2 → F41.2)
function normalizarDiagnostico(v) {
  const x = v.trim().toUpperCase();
  return CIE10.test(x) ? x : "-";
}

// Tipo: se acepta cualquier carácter único; si viene vacío o con más de un carácter → "S"
function normalizarTipo(v) {
  const x = v.trim();
  return Array.from(x).length === 1 ? x : "S";
}

// Cédula de 9 dígitos (perdió el cero inicial, p. ej. al pasar por Excel) → se antepone "0"
function normalizarCedula(v) {
  const x = v.trim();
  return /^\d{9}$/.test(x) ? "0" + x : x;
}

const CORRECCIONES = [
  { campo: "noDiagPri", corrige: normalizarDiagnostico },
  { campo: "noDiagSec", corrige: normalizarDiagnostico },
  { campo: "tipo",      corrige: normalizarTipo },
  { campo: "noPaciente", corrige: normalizarCedula },
];

// Mes anterior al actual en formato YYYY-MM (lo habitual al enviar atenciones)
function periodoAnterior() {
  const hoy = new Date();
  const d   = new Date(hoy.getFullYear(), hoy.getMonth() - 1, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

// Error generado en el navegador (no viene del backend): su mensaje se muestra tal cual
class ErrorLocal extends Error {}

// Escapa texto para usarlo dentro del HTML de SweetAlert
function escapar(texto) {
  return String(texto)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

export default {
  name: "AmbulatorioConversor",
  components: { Layout, Footer, DataTable },

  data() {
    return {
      modos:       MODOS,
      modo:        "xlsx_completo",
      arrastrando: false,
      cargando:    false,
      archivos:    [],   // { id, file, estado, filas, url, nombre, blob, mensaje }
      secuencia:   0,
      vista:       null, // { columnas, filas, total } del CSV generado
      vistaNombre: "",   // nombre del CSV mostrado en la vista previa
      validacionVista: null, // resultado de la validación del CSV mostrado (solo si tiene errores)
      vistaId:     null, // id del archivo mostrado en la vista previa
      swap:        { a: "", b: "" }, // columnas señaladas para intercambiar
      intercambiando: false,
      // Estado de la tabla de vista previa (orden, filtros por columna, filtro de validación y paginación)
      tabla: { pagina: 1, porPagina: 10, campoOrden: "", ascendente: true, filtros: {}, validacion: "" },
      conCabecera: true, // formatos completos: true = la primera fila es cabecera (se reemplaza); false = se agrega
      periodo:     periodoAnterior(), // formato YYYY-MM (input type="month")
      // Valores con los que se completan los campos vacíos (formatos "incompleto")
      valoresCentro: [
        { campo: "noDiagSec",     valor: "-" },
        { campo: "lMuerto",       valor: "0" },
        { campo: "fTarifario",    valor: "20250808" },
        { campo: "noServicio",    valor: "99215" },
        { campo: "cantidad",      valor: "1" },
        { campo: "vSem",          valor: "1" },
        { campo: "vPaciente",     valor: "0" },
        { campo: "noModificador", valor: "0" },
        { campo: "nombreMedIns",  valor: "." },
        { campo: "tipo",          valor: "S" },
      ],
      columnasEsperadas: [
        "noPlanilla", "codVal", "noPaciente", "nombre", "fIngreso", "fSalida",
        "noDiagPri", "noDiagSec", "lMuerto", "fTarifario", "noServicio", "cantidad",
        "vSem", "vPaciente", "noModificador", "nombreMedIns", "tipo", "fAplicacion",
      ],
    };
  },

  computed: {
    esCsv()        { return MODOS[this.modo].formato === "csv"; },
    esIncompleto() { return MODOS[this.modo].incompleto; },
    extensionModo() { return this.esCsv ? ".csv" : ".xlsx"; },

    acceptModo() {
      return this.esCsv
        ? ".csv,text/csv"
        : ".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
    },

    textoCabecera() {
      return this.conCabecera
        ? "reemplazando la primera fila (cabecera del archivo) por los encabezados estándar"
        : "agregando los encabezados estándar antes de la primera fila (el archivo no trae cabecera)";
    },

    // Año y mes seleccionados, o null si el período no es válido
    anioMes() {
      const m = /^(\d{4})-(\d{2})$/.exec(this.periodo || "");
      if (!m) return null;
      const anio = Number(m[1]);
      const mes  = Number(m[2]);
      return anio >= 2000 && anio <= 2100 && mes >= 1 && mes <= 12 ? { anio, mes } : null;
    },

    // Fechas que se colocarán (YYYYMMDD)
    fechasPeriodo() {
      if (!this.anioMes) return null;
      const { anio, mes } = this.anioMes;
      const ym     = `${anio}${String(mes).padStart(2, "0")}`;
      const ultimo = new Date(anio, mes, 0).getDate();
      return { ingreso: `${ym}01`, salida: `${ym}${String(ultimo).padStart(2, "0")}`, aplicacion: `${ym}01` };
    },

    archivosOk() {
      return this.archivos.filter(a => a.estado === "ok");
    },

    // Archivo cuyo CSV se muestra en la vista previa
    vistaItem() {
      return this.vistaId == null ? null : this.archivos.find(a => a.id === this.vistaId) || null;
    },

    puedeIntercambiar() {
      const { a, b } = this.swap;
      return !!this.vistaItem && !this.intercambiando && !!a && !!b && a !== b;
    },

    // ── Tabla de la vista previa ──
    // Valores distintos de la columna Validación ("OK", "Error: noDiagPri", ...) con su número de filas.
    // "OK" va primero y el resto ordenado de mayor a menor frecuencia.
    opcionesValidacion() {
      if (!this.vista) return [];
      const conteo = {};
      this.vista.filas.forEach(f => {
        const v = f._validacion;
        if (v) conteo[v] = (conteo[v] || 0) + 1;
      });
      return Object.keys(conteo)
        .sort((a, b) => {
          if (a === "OK") return -1;
          if (b === "OK") return 1;
          return conteo[b] - conteo[a] || a.localeCompare(b, "es");
        })
        .map(valor => ({ valor, texto: `${valor} (${conteo[valor]})` }));
    },

    // Filtra (texto por columna + lista de validación) y ordena TODAS las filas del archivo
    filasOrdenadas() {
      if (!this.vista) return [];
      const { filtros, validacion, campoOrden, ascendente } = this.tabla;
      const activos = Object.keys(filtros).filter(k => filtros[k]);

      let lista = this.vista.filas.filter(f => {
        if (validacion && f._validacion !== validacion) return false;
        return activos.every(k => String(f[k] ?? "").toLowerCase().includes(String(filtros[k]).toLowerCase()));
      });

      if (campoOrden) {
        const dir = ascendente ? 1 : -1;
        const esNumero = v => v !== "" && !isNaN(Number(v));
        lista = [...lista].sort((a, b) => {
          const va = a[campoOrden] ?? "";
          const vb = b[campoOrden] ?? "";
          if (esNumero(va) && esNumero(vb)) return (Number(va) - Number(vb)) * dir;
          return String(va).localeCompare(String(vb), "es") * dir;
        });
      }
      return lista;
    },

    totalPaginas() {
      return Math.max(1, Math.ceil(this.filasOrdenadas.length / this.tabla.porPagina));
    },

    paginaActual() {
      return Math.min(Math.max(1, this.tabla.pagina), this.totalPaginas);
    },

    filasPagina() {
      const ini = (this.paginaActual - 1) * this.tabla.porPagina;
      return this.filasOrdenadas.slice(ini, ini + this.tabla.porPagina);
    },

    // Páginas visibles con puntos suspensivos
    paginasVisibles() {
      const total  = this.totalPaginas;
      const actual = this.paginaActual;
      const rango  = [];
      for (let i = Math.max(2, actual - 2); i <= Math.min(total - 1, actual + 2); i++) rango.push(i);
      if (rango.length && rango[0] > 2)                        rango.unshift("...");
      if (rango.length && rango[rango.length - 1] < total - 1) rango.push("...");
      const paginas = [1];
      rango.forEach(p => paginas.push(p));
      if (total > 1) paginas.push(total);
      return paginas;
    },

    totalCorrecciones() {
      return this.archivosOk.reduce((suma, a) => suma + (a.correcciones ? a.correcciones.total : 0), 0);
    },

    totalErrores() {
      return this.archivosOk.reduce((suma, a) => suma + (a.validacion ? a.validacion.total : 0), 0);
    },

    columnasErrores() {
      return [
        { label: "Fila",    field: "fila" },
        { label: "Campo",   field: "campo" },
        { label: "Valor",   field: "valor" },
        { label: "Detalle", field: "detalle" },
      ];
    },

    erroresFilas() {
      return this.validacionVista
        ? this.validacionVista.errores.map((e, i) => ({ _id: i + 1, ...e }))
        : [];
    },

    // Archivos ya procesados (correctos o con error) en la tanda actual
    archivosHechos() {
      return this.archivos.filter(a => a.estado === "ok" || a.estado === "error").length;
    },

    totalFilas() {
      return this.archivosOk.reduce((suma, a) => suma + a.filas, 0);
    },

    puedeConvertir() {
      if (this.cargando) return false;
      if (!this.archivos.some(a => a.estado !== "ok")) return false;
      return !this.esIncompleto || this.anioMes !== null;
    },

    textoBoton() {
      if (this.cargando) return `Convirtiendo ${this.archivosHechos}/${this.archivos.length}...`;
      const pendientes = this.archivos.filter(a => a.estado !== "ok").length;
      return pendientes > 1 ? `Convertir ${pendientes} archivos` : "Convertir a CSV";
    },
  },

  watch: {
    // Lo ya convertido quedó con el valor anterior: se vuelve a pendiente para reprocesarlo
    periodo()     { if (this.esIncompleto)  this.reiniciarConvertidos(); },
    conCabecera() { if (!this.esIncompleto) this.reiniciarConvertidos(); },
  },

  mounted() {
    this.$refs.layoutRef.toggleProcesos();
    this.$refs.layoutRef.toggleReportes();
    this.$refs.layoutRef.toggleParametros();
    this.$refs.layoutRef.toggleAyuda();
  },

  beforeUnmount() {
    this.liberarArchivos();
  },

  methods: {
    // ── Selección y cola de archivos ──────────────────────────
    onSeleccionar(e) {
      this.agregarArchivos(e.target.files);
      e.target.value = ""; // permite volver a elegir los mismos archivos
    },

    onDrop(e) {
      this.arrastrando = false;
      this.agregarArchivos(e.dataTransfer.files);
    },

    // Devuelve el motivo de rechazo o "" si el archivo es válido
    validarArchivo(f) {
      const extension = this.esCsv ? /\.csv$/i : /\.xlsx$/i;
      if (!extension.test(f.name)) return `solo se admiten archivos ${this.extensionModo}.`;
      if (f.size === 0) return "el archivo está vacío.";
      if (f.size > MAX_MB * 1024 * 1024) return `supera el máximo de ${MAX_MB} MB.`;
      return "";
    },

    agregarArchivos(lista) {
      if (this.cargando) return;
      const rechazados = [];

      Array.from(lista).forEach(f => {
        const problema = this.validarArchivo(f);
        if (problema) {
          rechazados.push(`${escapar(f.name)}: ${problema}`);
          return;
        }
        const repetido = this.archivos.some(a =>
          a.file.name === f.name && a.file.size === f.size && a.file.lastModified === f.lastModified
        );
        if (repetido) return;

        this.archivos.push({
          id: ++this.secuencia,
          file: f,
          estado: "pendiente", // pendiente | procesando | ok | error
          filas: 0,
          url: "",
          nombre: "",
          blob: null,
          mensaje: "",
          validacion: null, // { total, errores, porFila, resumen, registros, filasConError }
          correcciones: null, // { total, porCampo } valores corregidos automáticamente
          intercambios: [],   // [{ a, b }] intercambios de columnas aplicados al CSV
          original: null,     // { blob, nombre, validacion, filas } del CSV antes del primer intercambio
        });
      });

      if (rechazados.length) {
        Swal.fire({ icon: "warning", title: "Archivos omitidos", html: rechazados.join("<br>") });
      }
    },

    reiniciarConvertidos() {
      if (this.cargando) return;
      let hubo = false;
      this.archivos.forEach(a => {
        if (a.estado !== "ok") return;
        if (a.url) URL.revokeObjectURL(a.url);
        Object.assign(a, { estado: "pendiente", url: "", blob: null, nombre: "", filas: 0, mensaje: "", validacion: null, correcciones: null, intercambios: [], original: null });
        hubo = true;
      });
      if (hubo) this.liberarVista();
    },

    quitarArchivo(item) {
      if (this.cargando) return;
      if (item.url) URL.revokeObjectURL(item.url);
      if (this.vistaNombre && this.vistaNombre === item.nombre) this.liberarVista();
      this.archivos = this.archivos.filter(a => a.id !== item.id);
    },

    cambiarModo(modo) {
      if (this.modo === modo) return;
      this.modo = modo;
      this.limpiar();
    },

    // ── Conversión ────────────────────────────────────────────
    // Procesa uno por uno (no satura el servidor) los pendientes o con error
    async convertir() {
      if (!this.puedeConvertir) return;
      this.cargando = true;
      this.liberarVista();

      try {
        for (const item of this.archivos) {
          if (item.estado === "ok") continue;
          item.estado = "procesando";
          item.mensaje = "";
          try {
            const r = await this.corregir(await this.procesar(item.file));
            if (item.url) URL.revokeObjectURL(item.url);
            item.blob   = r.blob;
            item.url    = r.url;
            item.nombre = r.nombre;
            item.correcciones = r.correcciones;
            item.validacion = await this.validarBlob(r.blob);
            // Filas = registros del CSV final; no se depende de la cabecera X-Filas-Procesadas del
            // backend (el navegador no la ve si el CORS no la expone)
            item.filas  = item.validacion ? item.validacion.registros : r.filas;
            item.estado = "ok";
          } catch (error) {
            item.estado  = "error";
            item.mensaje = await this.mensajeError(error);
          }
        }
      } finally {
        this.cargando = false;
      }

      const ok      = this.archivosOk.length;
      const errores = this.archivos.length - ok;
      const invalidos = this.totalErrores;
      const corregidos = this.totalCorrecciones;
      const obs = (corregidos ? ` Se corrigieron ${corregidos} valores (diagnóstico, tipo o cédula).` : "")
        + (invalidos ? ` Hay ${invalidos} errores de validación.` : "");

      // Un solo archivo: se descarga y se muestra de inmediato
      if (this.archivos.length === 1 && ok === 1) {
        const a = this.archivos[0];
        this.descargar(a.url, a.nombre);
        await this.verVista(a);
        Swal.fire({
          icon: invalidos ? "warning" : "success",
          title: invalidos ? "Archivo generado con observaciones" : "¡Correcto!",
          text: `Se generó ${a.nombre} (${a.filas} filas).${obs}`,
        });
      } else if (errores === 0) {
        Swal.fire({
          icon: invalidos ? "warning" : "success",
          title: invalidos ? "Conversión con observaciones" : "¡Correcto!",
          text: `Se convirtieron ${ok} archivos.${obs}`,
        });
      } else {
        Swal.fire({
          icon: "warning",
          title: "Conversión parcial",
          text: `Se convirtieron ${ok} archivos y ${errores} tuvieron errores.${obs} Revise el detalle en la lista.`,
        });
      }
    },

    // Aplica las correcciones al CSV ya generado (cualquiera de las cuatro opciones):
    // diagnósticos que no son CIE-10 → "-", tipo vacío o de más de un carácter → "S" y
    // cédula de 9 dígitos → se antepone "0". Si no hay nada que corregir, el archivo queda
    // exactamente como salió. "reglas" permite aplicar solo algunas (p. ej. tras un intercambio).
    async corregir(r, reglas = CORRECCIONES) {
      const sinCambios = { ...r, correcciones: { total: 0, porCampo: {} } };
      const texto = this.decodificar(await r.blob.arrayBuffer());
      const filas = this.parsearCsv(texto).filter(f => !f.every(c => c.trim() === ""));
      const encabezados = filas[0] || [];
      const pos = {};
      reglas.forEach(c => { pos[c.campo] = encabezados.indexOf(c.campo); });
      if (Object.values(pos).some(i => i < 0)) return sinCambios;

      const porCampo = {};
      let total = 0;
      filas.slice(1).forEach(f => {
        if (f.length !== encabezados.length) return; // la validación lo informa
        reglas.forEach(({ campo, corrige }) => {
          const original = f[pos[campo]];
          const nuevo = corrige(original);
          if (nuevo !== original.trim()) {
            f[pos[campo]] = nuevo;
            porCampo[campo] = (porCampo[campo] || 0) + 1;
            total++;
          }
        });
      });
      if (!total) return sinCambios;

      const sep = encabezados.length ? texto.replace(/^\uFEFF/, "").charAt(this.columnasEsperadas[0].length) : ";";
      const blob = new Blob(["\uFEFF" + this.serializarCsv(filas, sep)], { type: "text/csv" });
      URL.revokeObjectURL(r.url);
      return { ...r, blob, url: URL.createObjectURL(blob), correcciones: { total, porCampo } };
    },

    detalleCorrecciones(c) {
      const detalle = Object.keys(c.porCampo).map(k => `${k}: ${c.porCampo[k]}`).join(" · ");
      const reglas = [];
      if (c.porCampo.noDiagPri || c.porCampo.noDiagSec) reglas.push('diagnóstico que no es CIE-10 → "-"');
      if (c.porCampo.tipo) reglas.push('tipo vacío o de más de un carácter → "S"');
      if (c.porCampo.noPaciente) reglas.push('cédula de 9 dígitos → se antepone "0"');
      return `${detalle}. ${reglas.join("; ")}.`;
    },

    sumarCorrecciones(a, b) {
      const porCampo = { ...(a ? a.porCampo : {}) };
      Object.keys(b.porCampo).forEach(k => { porCampo[k] = (porCampo[k] || 0) + b.porCampo[k]; });
      return { total: (a ? a.total : 0) + b.total, porCampo };
    },

    // Cada opción tiene su propio procesamiento; todas devuelven { blob, url, nombre, filas }
    procesar(file) {
      if (this.esCsv) {
        return this.esIncompleto ? this.csvIncompleto(file) : this.csvCompleto(file);
      }
      return this.esIncompleto
        ? this.procesarArchivo(file, RUTA_INCOMPLETO, { anio: this.anioMes.anio, mes: this.anioMes.mes })
        : this.procesarArchivo(file, RUTA_COMPLETO, { conCabecera: this.conCabecera });
    },

    // XLSX → backend
    async procesarArchivo(file, ruta, params = {}) {
      const formData = new FormData();
      formData.append("file", file);
      Object.entries(params).forEach(([clave, valor]) => formData.append(clave, valor));

      const resp = await api.post(ruta, formData, { responseType: "blob" });
      return {
        blob:   resp.data,
        url:    URL.createObjectURL(resp.data),
        nombre: this.nombreDesdeHeader(resp.headers["content-disposition"], file.name),
        filas:  Number(resp.headers["x-filas-procesadas"] ?? 0),
      };
    },

    // CSV completo: si el archivo trae cabecera se reemplaza; si no, se agrega. La salida se arma
    // con los bytes originales (Blob), de modo que la codificación y los caracteres especiales
    // no se alteran.
    async csvCompleto(file) {
      const conCabecera = this.conCabecera;
      const buffer = await file.arrayBuffer();
      const { sep, datos } = this.analizarCsv(this.decodificar(buffer), conCabecera);

      const bytes = new Uint8Array(buffer);
      const esSalto = b => b === 10 || b === 13;
      const bom = bytes[0] === 0xEF && bytes[1] === 0xBB && bytes[2] === 0xBF ? 3 : 0;
      let ini = bom;
      while (ini < bytes.length && esSalto(bytes[ini])) ini++; // líneas en blanco iniciales
      let fin = ini;
      while (fin < bytes.length && !esSalto(bytes[fin])) fin++; // fin de la primera línea

      // Salto de línea del archivo, para que la cabecera sea coherente con el resto
      let eol = "\r\n";
      let resto = fin; // inicio de la segunda línea
      if (fin < bytes.length) {
        const doble = bytes[fin] === 13 && bytes[fin + 1] === 10;
        eol = doble ? "\r\n" : bytes[fin] === 13 ? "\r" : "\n";
        resto = fin + (doble ? 2 : 1);
      }

      const cabecera = this.columnasEsperadas.join(sep) + eol;
      const cuerpo = buffer.slice(conCabecera ? resto : ini);
      const blob = new Blob([buffer.slice(0, bom), cabecera, cuerpo], { type: "text/csv" });
      return this.resultadoCsv(blob, file.name.replace(/\.csv$/i, "") + "_cabeceras.csv", datos);
    },

    // CSV incompleto: la primera línea (cabecera) se reemplaza, se colocan las fechas del período
    // y los campos vacíos se completan con su valor por defecto
    async csvIncompleto(file) {
      const texto = this.decodificar(await file.arrayBuffer());
      const { sep } = this.analizarCsv(texto, true);
      const total = this.columnasEsperadas.length;

      const forzados = {
        fIngreso:    this.fechasPeriodo.ingreso,
        fSalida:     this.fechasPeriodo.salida,
        fAplicacion: this.fechasPeriodo.aplicacion,
      };
      const defectos = {};
      this.valoresCentro.forEach(v => { defectos[v.campo] = v.valor; });
      const colCedula = this.columnasEsperadas.indexOf("noPaciente");

      // Se descartan las filas vacías y la primera (cabecera original)
      const registros = this.parsearCsv(texto, sep)
        .filter(f => !f.every(c => c.trim() === ""))
        .slice(1);

      const salida = [this.columnasEsperadas.join(sep)];
      registros.forEach((campos, n) => {
        const v = campos.map(c => c.trim());
        if (v.length !== total) {
          throw new ErrorLocal(`Registro ${n + 1}: se esperaban ${total} columnas y hay ${v.length}.`);
        }
        this.columnasEsperadas.forEach((col, i) => {
          if (v[i] === "" && col in defectos) v[i] = defectos[col];
          if (col in forzados) v[i] = forzados[col];
        });
        // La cédula suele perder el cero inicial al pasar por Excel
        if (/^\d+$/.test(v[colCedula]) && v[colCedula].length < LARGO_CEDULA) {
          v[colCedula] = v[colCedula].padStart(LARGO_CEDULA, "0");
        }
        salida.push(v.map(c => this.escaparCsv(c, sep)).join(sep));
      });

      // BOM UTF-8 para que Excel muestre bien las tildes (igual que los CSV del backend)
      const blob = new Blob(["\uFEFF" + salida.join("\r\n") + "\r\n"], { type: "text/csv" });
      return this.resultadoCsv(blob, file.name.replace(/\.csv$/i, "") + "_completado.csv", salida.length - 1);
    },

    resultadoCsv(blob, nombre, filas) {
      return { blob, url: URL.createObjectURL(blob), nombre, filas };
    },

    // Valida el CSV (18 columnas), detecta su separador y comprueba que la primera línea
    // sea (o no) una cabecera según lo elegido. Devuelve el separador y el nº de filas de datos.
    analizarCsv(texto, conCabecera) {
      const total = this.columnasEsperadas.length;
      const lineas = texto.split(/\r\n|\n|\r/).filter(l => l.trim() !== "");
      if (!lineas.length) throw new ErrorLocal("El archivo CSV está vacío.");
      const primera = lineas[0];

      // Separador: el que da exactamente el número de columnas esperado
      const sep = SEPARADORES.find(s => this.contarCampos(primera, s) === total);
      if (!sep) {
        const maximo = Math.max(...SEPARADORES.map(s => this.contarCampos(primera, s)));
        throw new ErrorLocal(`Se esperaban ${total} columnas y el archivo tiene ${maximo}.`);
      }

      // noPlanilla siempre lleva algún dígito; si el primer campo es solo texto, es una cabecera.
      // Evita descartar un dato por error (o dejar una cabecera vieja como si fuera un dato).
      const primerCampo = primera.split(sep)[0].replace(/"/g, "").trim();
      const pareceCabecera = primerCampo !== "" && !/\d/.test(primerCampo);
      if (conCabecera && !pareceCabecera) {
        throw new ErrorLocal(this.esIncompleto
          ? "La primera línea no parece una cabecera (en los archivos incompletos siempre debe venir)."
          : "La primera línea no parece una cabecera. Si el archivo no la trae, elija «Sin cabecera».");
      }
      if (!conCabecera && pareceCabecera) {
        throw new ErrorLocal("La primera línea parece una cabecera. Si el archivo la trae, elija «Con cabecera».");
      }

      const datos = lineas.length - (conCabecera ? 1 : 0);
      if (datos === 0) throw new ErrorLocal("El archivo no tiene filas de datos.");
      return { sep, datos };
    },

    // UTF-8 y, si el archivo no lo es, Windows-1252 (frecuente en CSV exportados desde Excel)
    decodificar(buffer) {
      try {
        return new TextDecoder("utf-8", { fatal: true }).decode(buffer);
      } catch {
        return new TextDecoder("windows-1252").decode(buffer);
      }
    },

    // Número de campos de una línea para un separador, ignorando los que estén entre comillas
    contarCampos(linea, sep) {
      let campos = 1;
      let entreComillas = false;
      for (const c of linea) {
        if (c === '"') entreComillas = !entreComillas;
        else if (c === sep && !entreComillas) campos++;
      }
      return campos;
    },

    escaparCsv(valor, sep) {
      return /["\r\n]/.test(valor) || valor.includes(sep)
        ? `"${valor.replace(/"/g, '""')}"`
        : valor;
    },

    // ── Descargas y vista previa ──────────────────────────────
    // Los navegadores pueden pedir permiso para descargar varios archivos seguidos
    async descargarTodos() {
      for (const a of this.archivosOk) {
        this.descargar(a.url, a.nombre);
        await new Promise(resolve => setTimeout(resolve, 400));
      }
    },

    async verVista(item) {
      if (this.vistaId !== item.id) this.swap = { a: "", b: "" };
      await this.cargarVista(item.blob, item.validacion);
      this.vistaNombre = this.vista ? item.nombre : "";
      this.vistaId = this.vista ? item.id : null;
      this.validacionVista = this.vista && item.validacion && item.validacion.total ? item.validacion : null;
    },

    // Un fallo aquí no debe afectar la descarga, que ya se realizó
    async cargarVista(blob, validacion) {
      try {
        const texto = this.decodificar(await blob.arrayBuffer());
        this.vista = this.construirVista(texto, validacion);
        this.reiniciarTabla();
      } catch (e) {
        console.error("No se pudo generar la vista previa:", e);
        this.vista = null;
      }
    },

    // Se guardan todas las filas como objetos; la tabla de la vista previa las filtra, ordena y pagina
    construirVista(texto, validacion) {
      const filas = this.parsearCsv(texto);
      const encabezados = filas[0] || [];
      const datos = filas.slice(1);

      const columnas = [{ label: "#", field: "_id" }];
      if (validacion) columnas.push({ label: "Validación", field: "_validacion" });
      encabezados.forEach(h => columnas.push({ label: h, field: h }));

      const filasObj = datos.map((valores, i) => {
        const fila = { _id: i + 1 };
        if (validacion) {
          const campos = validacion.porFila[i + 1] || [];
          fila._errores = campos;
          fila._validacion = campos.length ? `Error: ${campos.join(", ")}` : "OK";
        }
        encabezados.forEach((h, c) => { fila[h] = valores[c] ?? ""; });
        return fila;
      });
      return { columnas, filas: filasObj, validacion, total: datos.length };
    },

    // ── Validación del CSV generado ───────────────────────────
    // Se valida el resultado final (de cualquiera de las cuatro opciones), no el archivo de origen
    async validarBlob(blob) {
      try {
        const texto = this.decodificar(await blob.arrayBuffer());
        return this.validarFilas(this.parsearCsv(texto));
      } catch (e) {
        console.error("No se pudo validar el archivo:", e);
        return null;
      }
    },

    // filas[0] = encabezados; el resto son los registros. Devuelve el conteo total de errores,
    // los primeros MAX_ERRORES_LISTA con detalle, los campos con error por fila y un resumen por campo.
    validarFilas(filas) {
      const encabezados = filas[0] || [];
      const total = this.columnasEsperadas.length;
      const pos = {};
      this.columnasEsperadas.forEach(c => { pos[c] = encabezados.indexOf(c); });

      const errores = [];
      const porFila = {};
      const conteo = {};
      let cantidad = 0;
      let registros = 0;

      const registrar = (n, campo, valor, detalle) => {
        cantidad++;
        conteo[campo] = (conteo[campo] || 0) + 1;
        (porFila[n] = porFila[n] || []).push(campo);
        if (errores.length < MAX_ERRORES_LISTA) errores.push({ fila: n, campo, valor, detalle });
      };

      filas.slice(1).forEach((campos, i) => {
        if (campos.every(c => c.trim() === "")) return;
        const n = i + 1; // número de fila de datos (sin contar la cabecera)
        registros++;

        if (campos.length !== total) {
          registrar(n, "(fila)", `${campos.length} columnas`, `se esperaban ${total} columnas`);
          return;
        }

        const valores = {};
        this.columnasEsperadas.forEach(campo => {
          const v = (campos[pos[campo]] ?? "").trim();
          valores[campo] = v;
          const regla = REGLAS[campo];
          if (!regla) return;
          if (v === "") {
            if (regla.obligatorio) registrar(n, campo, "", "campo obligatorio");
          } else if (regla.valida && !regla.valida(v)) {
            registrar(n, campo, v, regla.detalle);
          }
        });

        // La salida no puede ser anterior al ingreso
        if (fechaValida(valores.fIngreso) && fechaValida(valores.fSalida) && valores.fSalida < valores.fIngreso) {
          registrar(n, "fSalida", valores.fSalida, "no puede ser anterior a fIngreso");
        }
      });

      return {
        total: cantidad,
        errores,
        porFila,
        registros,
        filasConError: Object.keys(porFila).length,
        resumen: Object.keys(conteo).map(campo => ({ campo, cantidad: conteo[campo] })),
      };
    },

    // Lector CSV (RFC 4180): comillas, comillas dobles escapadas y saltos de línea dentro de campos.
    // Sin separador explícito, se detecta en el encabezado: el carácter que sigue al primer nombre de columna.
    parsearCsv(texto, separador) {
      if (texto.charCodeAt(0) === 0xFEFF) texto = texto.slice(1); // BOM
      const sep = separador || texto.charAt(this.columnasEsperadas[0].length);
      const filas = [];
      let fila = [];
      let campo = "";
      let entreComillas = false;

      for (let i = 0; i < texto.length; i++) {
        const c = texto[i];
        if (entreComillas) {
          if (c === '"') {
            if (texto[i + 1] === '"') { campo += '"'; i++; }
            else entreComillas = false;
          } else {
            campo += c;
          }
        } else if (c === '"') {
          entreComillas = true;
        } else if (c === sep) {
          fila.push(campo);
          campo = "";
        } else if (c === "\n" || c === "\r") {
          if (c === "\r" && texto[i + 1] === "\n") i++;
          fila.push(campo);
          filas.push(fila);
          fila = [];
          campo = "";
        } else {
          campo += c;
        }
      }
      if (campo !== "" || fila.length) {
        fila.push(campo);
        filas.push(fila);
      }
      return filas;
    },

    // ── Utilidades ────────────────────────────────────────────
    tamano(bytes) {
      return `${(bytes / 1024).toFixed(1)} KB`;
    },

    badgeEstado(estado) {
      return {
        "bg-secondary": estado === "pendiente",
        "bg-info text-dark": estado === "procesando",
        "bg-success": estado === "ok",
        "bg-danger": estado === "error",
      };
    },

    textoEstado(estado) {
      return { pendiente: "Pendiente", procesando: "Procesando", ok: "Convertido", error: "Error" }[estado];
    },

    descargar(url, nombre) {
      const a = document.createElement("a");
      a.href = url;
      a.download = nombre;
      document.body.appendChild(a);
      a.click();
      a.remove();
    },

    nombreDesdeHeader(header, nombreOriginal) {
      const respaldo = nombreOriginal.replace(/\.xlsx$/i, "") + ".csv";
      if (!header) return respaldo;
      const utf8 = /filename\*=UTF-8''([^;]+)/i.exec(header);
      if (utf8) return decodeURIComponent(utf8[1]);
      const simple = /filename="?([^";]+)"?/i.exec(header);
      return simple ? simple[1] : respaldo;
    },

    // Con responseType "blob" el cuerpo del error también llega como Blob
    async mensajeError(error) {
      if (error instanceof ErrorLocal) return error.message;
      const data = error.response?.data;
      if (data instanceof Blob) {
        try {
          const json = JSON.parse(await data.text());
          if (json?.message) return json.message;
        } catch { /* sin JSON */ }
      } else if (data?.message) {
        return data.message;
      }
      // 413 sin mensaje del backend (p. ej. lo rechaza el servidor web o el proxy antes de llegar a Spring)
      if (error.response?.status === 413) return "El archivo supera el tamaño máximo permitido.";
      return error.response ? "Inténtelo de nuevo." : "No se pudo conectar con el servidor.";
    },

    liberarVista() {
      this.vista = null;
      this.vistaNombre = "";
      this.validacionVista = null;
      this.vistaId = null;
      this.swap = { a: "", b: "" };
      this.reiniciarTabla();
    },

    // ── Intercambio de columnas ───────────────────────────────
    esColumnaDato(campo) {
      return this.columnasEsperadas.includes(campo);
    },

    esColumnaSwap(campo) {
      return campo === this.swap.a || campo === this.swap.b;
    },

    // Señala / quita una columna. Con dos ya señaladas, la nueva sustituye a la más antigua.
    alternarColumnaSwap(campo) {
      if (this.intercambiando) return;
      if (this.swap.a === campo) this.swap.a = "";
      else if (this.swap.b === campo) this.swap.b = "";
      else if (!this.swap.a) this.swap.a = campo;
      else if (!this.swap.b) this.swap.b = campo;
      else this.swap = { a: this.swap.b, b: campo };
    },

    limpiarSwap() {
      this.swap = { a: "", b: "" };
    },

    textoIntercambios(item) {
      return item.intercambios.map(i => `${i.a} ↔ ${i.b}`).join(" · ");
    },

    // Intercambia los VALORES de dos columnas del CSV generado (la cabecera estándar no cambia),
    // actualiza el CSV del archivo, lo vuelve a validar y refresca la vista previa.
    // No se reaplican las correcciones automáticas: así el intercambio es reversible.
    async intercambiarColumnas() {
      const item = this.vistaItem;
      if (!item || !this.puedeIntercambiar) return;
      const { a, b } = this.swap;
      this.intercambiando = true;
      try {
        const texto = this.decodificar(await item.blob.arrayBuffer());
        const filas = this.parsearCsv(texto).filter(f => !f.every(c => c.trim() === ""));
        const encabezados = filas[0] || [];
        const ia = encabezados.indexOf(a);
        const ib = encabezados.indexOf(b);
        if (ia < 0 || ib < 0) throw new ErrorLocal("No se encontraron las columnas seleccionadas en el CSV.");

        filas.slice(1).forEach(f => {
          if (f.length !== encabezados.length) return; // la validación informa las filas mal formadas
          const tmp = f[ia];
          f[ia] = f[ib];
          f[ib] = tmp;
        });

        const sep = texto.replace(/^\uFEFF/, "").charAt(this.columnasEsperadas[0].length);
        const intercambiado = new Blob(["\uFEFF" + this.serializarCsv(filas, sep)], { type: "text/csv" });

        // Una cédula de 9 dígitos que quede en noPaciente tras el intercambio se completa con "0"
        const r = await this.corregir(
          { blob: intercambiado, url: URL.createObjectURL(intercambiado) },
          CORRECCIONES.filter(c => c.campo === "noPaciente")
        );
        const blob = r.blob;

        // Se conserva el CSV de antes del primer intercambio para poder restaurarlo
        if (!item.original) {
          item.original = {
            blob: item.blob, nombre: item.nombre, validacion: item.validacion, filas: item.filas,
            correcciones: item.correcciones ? { total: item.correcciones.total, porCampo: { ...item.correcciones.porCampo } } : null,
          };
        }
        if (item.url) URL.revokeObjectURL(item.url);
        item.blob = blob;
        item.url = r.url;
        if (r.correcciones.total) item.correcciones = this.sumarCorrecciones(item.correcciones, r.correcciones);
        item.nombre = item.original.nombre.replace(/\.csv$/i, "") + "_intercambio.csv";
        item.intercambios.push({ a, b });
        item.validacion = await this.validarBlob(blob);
        if (item.validacion) item.filas = item.validacion.registros;

        this.swap = { a: "", b: "" };
        await this.verVista(item);
        Swal.fire({
          icon: "success",
          title: "Columnas intercambiadas",
          text: `${a} ↔ ${b}. Revise la vista previa y use «Exportar CSV».`,
        });
      } catch (error) {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: error instanceof ErrorLocal ? error.message : "No se pudo intercambiar las columnas.",
        });
      } finally {
        this.intercambiando = false;
      }
    },

    // Descarga el CSV que se está mostrando (con los intercambios aplicados)
    exportarVista() {
      const item = this.vistaItem;
      if (item) this.descargar(item.url, item.nombre);
    },

    async restaurarOriginal() {
      const item = this.vistaItem;
      if (!item || !item.original) return;
      const o = item.original;
      if (item.url) URL.revokeObjectURL(item.url);
      item.blob = o.blob;
      item.url = URL.createObjectURL(o.blob);
      item.nombre = o.nombre;
      item.validacion = o.validacion;
      item.filas = o.filas;
      item.correcciones = o.correcciones;
      item.intercambios = [];
      item.original = null;
      this.swap = { a: "", b: "" };
      await this.verVista(item);
    },

    serializarCsv(filas, sep) {
      return filas.map(f => f.map(c => this.escaparCsv(c, sep)).join(sep)).join("\r\n") + "\r\n";
    },

    // ── Tabla de la vista previa ──────────────────────────────
    reiniciarTabla() {
      this.tabla = { pagina: 1, porPagina: this.tabla.porPagina, campoOrden: "", ascendente: true, filtros: {}, validacion: "" };
    },

    ordenarVistaPor(campo) {
      if (this.tabla.campoOrden === campo) {
        this.tabla.ascendente = !this.tabla.ascendente;
      } else {
        this.tabla.campoOrden = campo;
        this.tabla.ascendente = true;
      }
      this.tabla.pagina = 1;
    },

    iconOrden(campo) {
      if (this.tabla.campoOrden !== campo) return "⇅";
      return this.tabla.ascendente ? "↑" : "↓";
    },

    irAPagina(pagina) {
      if (pagina < 1 || pagina > this.totalPaginas) return;
      this.tabla.pagina = pagina;
    },

    liberarArchivos() {
      this.archivos.forEach(a => { if (a.url) URL.revokeObjectURL(a.url); });
      this.archivos = [];
    },

    limpiar() {
      this.liberarVista();
      this.liberarArchivos();
      if (this.$refs.inputArchivo) this.$refs.inputArchivo.value = "";
    },
  },
};
</script>

<style scoped>
.th-sort {
  cursor: pointer;
  user-select: none;
  white-space: nowrap;
}
.th-sort:hover { background-color: #3d5166; }
/* Columnas señaladas para intercambiar */
.swap-btn { color: inherit; opacity: .55; line-height: 1; }
.swap-btn:hover, .swap-btn.activo { opacity: 1; }
.th-swap { background-color: #ffc107 !important; color: #000 !important; }
.th-swap:hover { background-color: #ffca2c !important; }
.td-swap { background-color: rgba(255, 193, 7, .25) !important; }
/* Celdas en una sola línea; si la tabla es más ancha que la pantalla, se desplaza en horizontal */
.tabla-sin-saltos {
  overflow-x: auto;
}
.tabla-sin-saltos :deep(th),
.tabla-sin-saltos :deep(td) {
  white-space: nowrap !important;
}
</style>